import { HtBotActionNode } from '../../domain/models/cms/hubtype/bot-action'
import { HtCarouselNode } from '../../domain/models/cms/hubtype/carousel'
import { HtFallbackNode } from '../../domain/models/cms/hubtype/fallback'
import { HtFunctionNode } from '../../domain/models/cms/hubtype/function'
import { HtGoToFlowNode } from '../../domain/models/cms/hubtype/go-to-flow'
import { HtHandoffNode } from '../../domain/models/cms/hubtype/handoff'
import { HtImageNode } from '../../domain/models/cms/hubtype/image'
import { HtKeywordNode } from '../../domain/models/cms/hubtype/keyword'
import {
  HtNodeComponent,
  HtNodeWithContent,
  HtNodeWithoutContent,
} from '../../domain/models/cms/hubtype/nodes'
import { HtTextNode } from '../../domain/models/cms/hubtype/text'
import { HtVideoNode } from '../../domain/models/cms/hubtype/video'
import { HtWhatsappButtonListNode } from '../../domain/models/cms/hubtype/whatsapp'
import {
  ContentType,
  PayloadFields,
  UrlFields,
} from '../../domain/models/content-fields'
import {
  CustomConditionNodeInterface,
  VariableFormat,
} from '../../nodes/custom-conditional'
import { KnowledgeBaseNodeInterface } from '../../nodes/knowledge-base'
import { HtKnowledgeBaseNode } from '../../nodes/knowledge-base/ht-model'
import { SmartIntentNodeInterface } from '../../nodes/smart-intent'
import { HtSmartIntentNode } from '../../nodes/smart-intent/ht-model'
import { WhatsappCTAUrlButtonNodeInterface } from '../../nodes/whatsapp-cta-url-button'
import { HtWhatsappCTAUrlButtonNode } from '../../nodes/whatsapp-cta-url-button/ht-model'
import {
  QUEUE_CLOSED,
  QUEUE_OPEN,
  QUEUE_OPEN_WITHOUT_AGENTS,
} from '../../UI/constants'
import {
  BotActionNode,
  CarouselNode,
  ChannelNode,
  CountryConditionNode,
  FallbackNode,
  GoToFlowNode,
  HandoffNode,
  ImageNode,
  KeywordNode,
  NodeTypes,
  NonMessageContents,
  QueueStatusNode,
  State,
  TextNode,
  VideoNode,
  WhatsappButtonListNode,
} from '../../UI/types'
import { HtWebviewComponent } from '../../webviews/ht-model'
import { isWebview } from '../../webviews/utils'
import { LinkFactory } from './hubtype-links-factory'

export class NodeFactory {
  private edgeFactory
  constructor() {
    this.edgeFactory = new LinkFactory()
  }

  public getHubtypeNodes(state: State): {
    hubtypeNodes: HtNodeComponent[]
    webviewContent: HtWebviewComponent[]
  } {
    let hubtypeNodes: HtNodeComponent[] = []
    const nonMessageNodes = this.getNonMessageNodes(state.nonMessageContents)
    const messageNodes = state.nodes
      .filter(
        node =>
          node.data.contentType() !== ContentType.START &&
          !isWebview(node.data.flowId)
      )
      .map(node => this.getMessageNodes(node, state))
    const webviewContent = this.getWebviewContent(state)
    hubtypeNodes = [...nonMessageNodes, ...messageNodes]
    return {
      hubtypeNodes,
      webviewContent,
    }
  }

  private getWebviewContent(state: State): HtWebviewComponent[] {
    //@ts-expect-error issue detecting node type
    return state.nodes
      .filter(
        node =>
          (node.data.contentType() === ContentType.TEXT ||
            node.data.contentType() === ContentType.IMAGE) &&
          isWebview(node.data.flowId)
      )
      .map(node => this.getMessageNodes(node, state)) as HtWebviewComponent[]
  }

  private getNonMessageNodes(
    nonMessageContents: NonMessageContents
  ): HtNodeWithoutContent[] {
    const urls = this.convertNonMessageContents(nonMessageContents.urls)
    const payloads = this.convertNonMessageContents(nonMessageContents.payloads)
    const noneMessageNodes = [...urls, ...payloads]
    return noneMessageNodes
  }

  private getMessageNodes(node: NodeTypes, state: State): HtNodeWithContent {
    switch (node.type) {
      case ContentType.TEXT:
        return this.getText(node, state)
      case ContentType.IMAGE:
        return this.getImage(node, state)
      case ContentType.CAROUSEL:
        return this.getCarousel(node, state)
      case ContentType.QUEUE_STATUS:
        return this.getQueueStatus(node, state)
      case ContentType.CHANNEL:
        return this.getChannel(node, state)
      case ContentType.COUNTRY_CONDITION:
        return this.getCountry(node, state)
      case ContentType.CUSTOM_CONDITION:
        return this.getCustomCondition(node, state)
      case ContentType.FALLBACK:
        return this.getFallback(node, state)
      case ContentType.WHATSAPP_BUTTON_LIST:
        return this.getWhatsappButtonList(node, state)
      case ContentType.HANDOFF:
        return this.getHandoff(node, state)
      case ContentType.VIDEO:
        return this.getVideo(node, state)
      case ContentType.KEYWORD:
        return this.getKeywords(node, state)
      case ContentType.SMART_INTENT:
        return this.getSmartIntent(node, state)
      case ContentType.GO_TO_FLOW:
        return this.getGoToFlow(node, state)
      case ContentType.BOT_ACTION:
        return this.getBotAction(node, state)
      case ContentType.WHATSAPP_CTA_URL_BUTTON:
        return this.getWhatsappCTAUrlButton(node, state)
      case ContentType.KNOWLEDGE_BASE:
        return this.getKnowledgeBase(node, state)
      default:
        throw new Error(`This content is not supported`)
    }
  }

  private getNode(node: NodeTypes, state: State): HtNodeWithContent {
    const locale = state.currentLocale.code
    const hubtypeNode = node.data.toHubtypeCMS(node.position, locale)
    this.edgeFactory.addNodeLink(node.data.edges, hubtypeNode)
    return hubtypeNode
  }

  private getText(node: TextNode, state: State): HtTextNode {
    const hubtypeNode = this.getNode(node, state) as HtTextNode
    hubtypeNode.content.buttons = hubtypeNode.content.buttons?.map(button => {
      this.edgeFactory.addFieldLink(node, button)
      return button
    })
    return hubtypeNode
  }

  private getImage(node: ImageNode, state: State): HtImageNode {
    const hubtypeNode = this.getNode(node, state) as HtImageNode
    return hubtypeNode
  }

  private getCarousel(node: CarouselNode, state: State): HtCarouselNode {
    const hubtypeNode = this.getNode(node, state) as HtCarouselNode
    hubtypeNode.content.elements.forEach(element => {
      this.edgeFactory.addFieldLink(node, element.button)
    })
    return hubtypeNode
  }

  private getQueueStatus(node: QueueStatusNode, state: State): HtFunctionNode {
    const hubtypeNode = this.getNode(node, state) as HtFunctionNode
    this.edgeFactory.addConditionLink(node.data.edges, hubtypeNode, QUEUE_OPEN)
    this.edgeFactory.addConditionLink(
      node.data.edges,
      hubtypeNode,
      QUEUE_CLOSED
    )
    this.edgeFactory.addConditionLink(
      node.data.edges,
      hubtypeNode,
      QUEUE_OPEN_WITHOUT_AGENTS
    )
    return hubtypeNode
  }

  private getChannel(node: ChannelNode, state: State): HtFunctionNode {
    const hubtypeNode = this.getNode(node, state) as HtFunctionNode
    this.edgeFactory.addConditionLink(node.data.edges, hubtypeNode, 'default')
    node.data.channels.forEach(channel => {
      this.edgeFactory.addConditionLink(
        node.data.edges,
        hubtypeNode,
        channel.id
      )
    })
    return hubtypeNode
  }

  private getCountry(node: CountryConditionNode, state: State): HtFunctionNode {
    const hubtypeNode = this.getNode(node, state) as HtFunctionNode
    this.edgeFactory.addConditionLink(node.data.edges, hubtypeNode, 'default')
    node.data.countries.forEach(country => {
      this.edgeFactory.addConditionLink(
        node.data.edges,
        hubtypeNode,
        country.id
      )
    })
    return hubtypeNode
  }

  private getCustomCondition(
    node: CustomConditionNodeInterface,
    state: State
  ): HtFunctionNode {
    const hubtypeNode = this.getNode(node, state) as HtFunctionNode
    if (node.data.variableFormat !== VariableFormat.BOOLEAN) {
      this.edgeFactory.addConditionLink(node.data.edges, hubtypeNode, 'default')
    }
    node.data.values.forEach(value => {
      this.edgeFactory.addConditionLink(
        node.data.edges,
        hubtypeNode,
        value.id,
        value.getFormattedValue(node.data.variableFormat)
      )
    })
    if (node.data.variableFormat === VariableFormat.BOOLEAN) {
      const resultMapping = {
        result: 'default',
        target: hubtypeNode.content.result_mapping[1].target,
      }
      hubtypeNode.content.result_mapping.unshift(resultMapping)
    }
    return hubtypeNode
  }

  private getFallback(node: FallbackNode, state: State): HtFallbackNode {
    const hubtypeNode = node.data.toHubtypeCMS(node.position)
    this.edgeFactory.addFallbackLinks(node.data.edges, hubtypeNode)
    return hubtypeNode
  }

  private getSmartIntent(
    node: SmartIntentNodeInterface,
    state: State
  ): HtSmartIntentNode {
    const hubtypeNode = this.getNode(node, state) as HtSmartIntentNode
    return hubtypeNode
  }

  private getKeywords(node: KeywordNode, state: State): HtKeywordNode {
    const hubtypeNode = this.getNode(node, state) as HtKeywordNode
    return hubtypeNode
  }

  private getVideo(node: VideoNode, state: State): HtVideoNode {
    const hubtypeNode = this.getNode(node, state) as HtVideoNode
    return hubtypeNode
  }

  private getHandoff(node: HandoffNode, state: State): HtHandoffNode {
    const hubtypeNode = this.getNode(node, state) as HtHandoffNode
    return hubtypeNode
  }

  private getGoToFlow(node: GoToFlowNode, state: State): HtGoToFlowNode {
    const hubtypeNode = this.getNode(node, state) as HtGoToFlowNode
    return hubtypeNode
  }

  private getBotAction(node: BotActionNode, state: State): HtBotActionNode {
    const hubtypeNode = this.getNode(node, state) as HtBotActionNode
    return hubtypeNode
  }

  private getWhatsappButtonList(
    node: WhatsappButtonListNode,
    state: State
  ): HtWhatsappButtonListNode {
    const hubtypeNode = this.getNode(node, state) as HtWhatsappButtonListNode
    hubtypeNode.content.sections.forEach((section, sectionIndex) => {
      section.rows.forEach(row => {
        this.edgeFactory.addFieldLink(node, row)
      })
    })
    return hubtypeNode
  }

  private getWhatsappCTAUrlButton(
    node: WhatsappCTAUrlButtonNodeInterface,
    state: State
  ): HtWhatsappCTAUrlButtonNode {
    const hubtypeNode = this.getNode(node, state) as HtWhatsappCTAUrlButtonNode
    this.edgeFactory.addFieldLink(node, hubtypeNode.content.button)
    return hubtypeNode
  }

  private getKnowledgeBase(
    node: KnowledgeBaseNodeInterface,
    state: State
  ): HtKnowledgeBaseNode {
    const hubtypeNode = this.getNode(node, state) as HtKnowledgeBaseNode
    return hubtypeNode
  }

  private convertNonMessageContents(
    nonMessageContent: UrlFields[] | PayloadFields[]
  ): HtNodeWithoutContent[] {
    return nonMessageContent.map(content => content.toHubtypeCMS())
  }
}
